import { CircularProgress, Theme, useTheme } from "@material-ui/core";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  SafetyDashboardApiFactory,
  TotalDriversWidgetDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { WidgetProps } from "../../../assets/maintenance-dashboard/common";
import { TotalVehiclesLegend } from "../../../assets/maintenance-dashboard/widgets/total-vehicles/TotalVehiclesLegend";
import { Widget } from "../../../assets/maintenance-dashboard/widgets/Widget";
import { SelectedDriversFilterContext } from "../common";
import { TotalDriversCircularIndicators } from "./TotalDriversCircularIndicators";

export const TotalDrivers = forwardRef(
  ({ className, icon, title, ...rest }: WidgetProps, ref) => {
    const [total, setTotal] = useState<TotalDriversWidgetDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const axios = useAxiosInstance();
    const theme = useTheme<Theme>();

    const filterContext = useContext(SelectedDriversFilterContext);
    useEffect(() => {
      if (!axios) {
        return;
      }

      setLoading(true);
      SafetyDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppSafetydashboardTotalactivedriversGet()
        .then((result) => {
          setTotal(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios]);

    const handleEldDisconnectionsClick = useCallback(() => {
      if (!filterContext || !total?.driversWithPhysicalEldDisconnections) {
        return;
      }

      filterContext.setSelectedFilter("WITH_PHYSICAL_ELD_DISCONNECTIONS");
    }, [filterContext, total]);

    const handleGeometrisEventsClick = useCallback(() => {
      if (!filterContext || !total?.driversWithHarshGeometrisEvents) {
        return;
      }

      filterContext.setSelectedFilter("WITH_HARSH_EVENTS");
    }, [filterContext, total]);

    const handleSpeedingEventsClick = useCallback(() => {
      if (!filterContext || !total?.driversWithSpeedingEvents) {
        return;
      }

      filterContext.setSelectedFilter("WITH_SPEEDING_EVENTS");
    }, [filterContext, total]);

    const handleUnresolvedDvirDefectsClick = useCallback(() => {
      if (!filterContext || !total?.driversWithUnresolvedDvirDefects) {
        return;
      }

      filterContext.setSelectedFilter("WITH_UNRESOLVED_DVIR_DEFECTS");
    }, [filterContext, total]);

    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className="total-vehicles categories">
          {loading ? (
            <div className="category">
              <CircularProgress size="3rem" />
            </div>
          ) : (
            <>
              <TotalDriversCircularIndicators total={total} />
              <div className="total-vehicles-categories">
                <TotalVehiclesLegend
                  label="With physical ELD disconnections"
                  value={total?.driversWithPhysicalEldDisconnections}
                  borderColor={theme.palette.error.main}
                  onClick={handleEldDisconnectionsClick}
                />
                <TotalVehiclesLegend
                  label="With harsh events"
                  value={total?.driversWithHarshGeometrisEvents}
                  borderColor={theme.palette.warning.main}
                  onClick={handleGeometrisEventsClick}
                />
                <TotalVehiclesLegend
                  label="Found speeding"
                  value={total?.driversWithSpeedingEvents}
                  borderColor={theme.palette.error.main}
                  onClick={handleSpeedingEventsClick}
                />
                <TotalVehiclesLegend
                  label="With unresolved DVIR defects"
                  value={total?.driversWithUnresolvedDvirDefects}
                  borderColor={theme.palette.warning.main}
                  onClick={handleUnresolvedDvirDefectsClick}
                />
              </div>
            </>
          )}
        </div>
      </Widget>
    );
  }
);
